/**
 * Live calls (plan E03-T04): the calls in progress on the Calls page, and a call followed as it
 * happens in the inspector, both from the event stream.
 */
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Radio } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { openEventStream, type StreamedEvent, type StreamState } from "../api/events";
import { useApiQuery } from "../app/Page";
import { useBff, useMe } from "../app/session";
import { useFormat, useT } from "../i18n";
import { Table } from "../ui/Table";
import { Ladder, Timeline, Transcript, Turns, type Line } from "./inspector/parts";
import { ladder, timeline, turns, type CallEvent } from "./inspector/timeline";
import { Monitor } from "./Monitor";

/** Follow the event stream while mounted. */
export function useEventStream(
  options: { types?: string[]; callId?: string; since?: string; enabled?: boolean },
  onEvent: (event: StreamedEvent) => void,
): StreamState {
  const bff = useBff();
  const [state, setState] = useState<StreamState>("connecting");
  const handler = useRef(onEvent);
  useEffect(() => {
    handler.current = onEvent;
  }, [onEvent]);
  const key = JSON.stringify([options.types, options.callId, options.since]);
  useEffect(() => {
    if (options.enabled === false) return;
    const stream = openEventStream(bff, { ...options, onEvent: (e) => handler.current(e), onState: setState });
    return () => stream.close();
    // the stream is keyed by its filters; the handler is read through a ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bff, key, options.enabled]);
  return state;
}

function StreamBadge({ state }: { state: StreamState }) {
  const t = useT();
  return (
    <span role="status" className="inline-flex items-center gap-1 text-xs text-[var(--color-muted)]">
      <Radio size={12} aria-hidden="true" className={state === "live" ? "text-[var(--color-success)]" : ""} />
      {t(`live.state.${state}`)}
    </span>
  );
}

/** The Calls page's "Live now": refreshed as calls change state or end. */
export function LiveNow() {
  const t = useT();
  const format = useFormat();
  const queries = useQueryClient();
  const tenant = useMe().tenant_id;
  const live = useApiQuery("calls:live", (api) => api.GET("/v1/calls/live"));
  const refresh = useRef<ReturnType<typeof setTimeout>[]>([]);
  const state = useEventStream({ types: ["call.state_changed", "call.completed"] }, (e) => {
    refresh.current.forEach(clearTimeout);
    // a burst of changes, one reload; and one more once the recorder has surely stored them (the
    // list is read from the stored events, written a second or two after they are streamed)
    refresh.current = [400, 2500].map((ms) => setTimeout(() => void live.refetch(), ms));
    if (e.type === "call.completed") void queries.invalidateQueries({ queryKey: ["calls", tenant] });
  });
  useEffect(() => () => refresh.current.forEach(clearTimeout), []);
  const rows = live.data?.data ?? [];
  return (
    <section aria-labelledby="live-now" className="space-y-2">
      <div className="flex items-center gap-3">
        <h2 id="live-now" className="text-lg font-semibold">{t("live.title")}</h2>
        <StreamBadge state={state} />
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-[var(--color-muted)]">{t("live.none")}</p>
      ) : (
        <Table
          caption={t("live.title")}
          rows={rows}
          rowKey={(c) => c.call_id}
          columns={[
            {
              header: t("live.since"),
              cell: (c) => (
                <Link to="/calls/$callId" params={{ callId: c.call_id }} className="text-[var(--color-primary)] underline">
                  {format.time(c.started_at)}
                </Link>
              ),
            },
            { header: t("calls.col.direction"), cell: (c) => (c.direction ? t(`calls.direction.${c.direction}`) : "—") },
            { header: t("calls.col.from"), cell: (c) => <span className="font-mono">{c.from ?? "—"}</span> },
            { header: t("calls.col.to"), cell: (c) => <span className="font-mono">{c.to ?? "—"}</span> },
            { header: t("live.state"), cell: (c) => c.state ?? "—" },
          ]}
        />
      )}
    </section>
  );
}

/** A call still in progress: its story, turns and transcript grow as its events arrive. */
export function LiveCall({ callId, initial, onEnded }: { callId: string; initial: CallEvent[]; onEnded: () => void }) {
  const t = useT();
  const [events, setEvents] = useState<CallEvent[]>(initial);
  const last = initial.at(-1)?.id;
  const state = useEventStream({ callId, since: last }, (e) => {
    setEvents((es) => (es.some((x) => x.id === e.id) ? es : [...es, e as CallEvent]));
    if (e.type === "call.completed") onEnded();
  });
  const lines: Line[] = events
    .filter((e) => e.type === "transcript.final")
    .map((e) => ({ seq: Number(e.data.seq), role: e.data.role as Line["role"], text: String(e.data.text ?? ""),
                   at: String(e.data.at ?? e.time), interrupted: Boolean(e.data.interrupted) }));  // prettier-ignore
  const started = events.find((e) => e.time)?.time ?? new Date().toISOString();
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <p className="font-medium">{t("live.inProgress")}</p>
        <StreamBadge state={state} />
      </div>
      <Monitor callId={callId} />
      <div className="grid gap-6 xl:grid-cols-2">
        <section aria-labelledby="live-timeline" className="space-y-3 rounded-lg border border-[var(--color-border)] p-4">
          <h2 id="live-timeline" className="font-semibold">{t("inspect.timeline")}</h2>
          <Timeline entries={timeline(events)} />
          <h2 className="pt-2 font-semibold">{t("inspect.turns")}</h2>
          <Turns turns={turns(events)} />
          <h2 className="pt-2 font-semibold">{t("inspect.sip")}</h2>
          <Ladder steps={ladder(events)} />
        </section>
        <section aria-labelledby="live-transcript" className="space-y-3 rounded-lg border border-[var(--color-border)] p-4">
          <h2 id="live-transcript" className="font-semibold">{t("inspect.transcript")}</h2>
          <Transcript lines={lines} startedAt={started} />
        </section>
      </div>
    </div>
  );
}
